import React, { Dispatch, useEffect, useMemo } from 'react';
import RadioBtnGroup from '../_parts/RadioBtnGroup';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import { IProjectDocType } from '../../../../redux/project/sliceProjectDocTypes';
import {
    appendFiles,
    invalidFilesSelector,
    precheckFile,
    removeFile,
    notPrecheckedFilesSelector,
    FileStateModel,
    precheckActiveSelector,
} from '../../../../redux/import/sliceImportFileUpload';
import { useTranslation } from 'react-i18next';
import { setSelectedDocType } from '../../../../redux/import/sliceImportDialog';
import UploadFile from '../_parts/UploadFile';
import i18n from '../../../../localization/i18n';
import { mdiInformation } from '@mdi/js';
import Message from '../_parts/Message';
import { useParams } from 'react-router-dom';
import { getFileExtensionsFromFormats, getFileTypeOrDefault } from '../../../../util/FileUtils';
import { defaultAllowedFileFormat } from '../../../../Constants';
import { IRelatedDocument } from '../_parts/DocumentRow';
import FilePrecheckStatus from './_parts/FilePrecheckStatus';

const docTypeLocalNameComparer = (a: IProjectDocType, b: IProjectDocType): -1 | 0 | 1 => {
    if (a.translations[i18n.language] === b.translations[i18n.language]) {
        return 0;
    }
    return a.translations[i18n.language] > b.translations[i18n.language] ? 1 : -1;
};

export default () => {
    const { replicaId } = useParams();
    const { t } = useTranslation('translations', { keyPrefix: 'uploadDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();

    const files = useSelector((_store: IAppReduxStore) => _store.projectFileUpload.fileModels);
    const notPrecheckedFiles = useSelector(notPrecheckedFilesSelector);
    const invalidFiles = useSelector(invalidFilesSelector);
    const precheckLoading = useSelector(precheckActiveSelector);
    const importDocTypes = useSelector((_store: IAppReduxStore) => _store.importDocTypes.data);
    const { selectedDocType } = useSelector((_store: IAppReduxStore) => _store.importDialog);

    useEffect(() => {
        if (importDocTypes.length === 1 && !selectedDocType) {
            onDocTypeSelect(importDocTypes[0].name);
        }
    }, [importDocTypes, selectedDocType]);

    useEffect(() => {
        precheckFiles(files);
    }, [selectedDocType]);

    useEffect(() => {
        precheckFiles(notPrecheckedFiles);
    }, [notPrecheckedFiles]);

    const precheckFiles = (
        models: ({
            id: string;
        } & FileStateModel)[],
    ) => {
        if (selectedDocType && models?.length) {
            models.forEach(model => {
                dispatch(
                    precheckFile({
                        replicaId: replicaId,
                        fileInfo: {
                            ...model,
                        },
                        docType: selectedDocType.name,
                    }),
                );
            });
        }
    };

    const onDocTypeSelect = (key: string) => {
        const newObject = importDocTypes.find(x => x.name === key);

        if (newObject) {
            dispatch(setSelectedDocType(newObject));
        }
    };

    const radioGroupOptions = useMemo(
        () =>
            (importDocTypes ?? [])
                .slice()
                .sort(docTypeLocalNameComparer)
                .map(x => ({
                    key: x.name,
                    label: x.translations[i18n.language] ?? x.name,
                    icon: {
                        path: x.icon,
                        color: x.color,
                    },
                })),
        [importDocTypes],
    );

    const documents = useMemo((): IRelatedDocument[] => {
        return files.map(fileModel => {
            const fileType = getFileTypeOrDefault(fileModel.fileName);
            return {
                universalId: fileModel.id,
                title: fileModel.fileName,
                size: fileModel.fileSize,
                iconProps: {
                    path: fileType.icon,
                    color: fileType.color,
                },
                statusIcon: <FilePrecheckStatus {...fileModel} />,
            };
        });
    }, [files]);

    const allowedFileExtensions = useMemo(() => {
        const allowedFormats = selectedDocType?.extendedConfig?.AllowedFileFormats?.split('|') ?? [
            defaultAllowedFileFormat,
        ];

        return getFileExtensionsFromFormats(allowedFormats).join(',');
    }, [selectedDocType]);

    return (
        <>
            {invalidFiles.length > 0 && (
                <Message
                    text={t('infoInvalid', { count: invalidFiles.length, total: files.length })}
                    icon={mdiInformation}
                />
            )}
            <UploadFile
                buttonLabel={t('addFiles')}
                documentsLabel={t('filesOverview', { count: documents.length })}
                acceptFileTypes={allowedFileExtensions}
                appendFilesInfo={appendFiles}
                removeFile={removeFile}
                uploadedDocuments={documents}
                allowMultiple={true}
            />
            <RadioBtnGroup
                disabled={precheckLoading}
                label={t(`step1`)}
                value={selectedDocType?.name ?? ''}
                onSelect={onDocTypeSelect}
                options={radioGroupOptions}
                containerSx={{ m: 0, marginTop: '16px' }}
                border={true}
            />
        </>
    );
};
